import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Hand, Plug, Boxes } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { formatApiError } from "@/context/AuthProvider";

const STATUS_META = {
  ok: { icon: CheckCircle2, cls: "text-success", badge: "bg-success/10 text-success" },
  fail: { icon: XCircle, cls: "text-destructive", badge: "bg-destructive/10 text-destructive" },
  pending: { icon: Clock, cls: "text-amber-500", badge: "bg-amber-500/10 text-amber-500" },
  manual: { icon: Hand, cls: "text-muted-foreground", badge: "bg-secondary text-muted-foreground" },
};

const CATS = ["config", "oauth", "embedded", "data", "webhooks", "compliance", "review"];

export function AdminShopify() {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shopify/review-checklist");
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  if (!data) return null;

  const s = data.summary || {};

  return (
    <div className="max-w-4xl" data-testid="admin-shopify-page">
      <h1 className="flex items-center gap-2 font-display text-2xl font-extrabold tracking-tighter">
        <Plug className="h-6 w-6 text-[#5E8E3E]" />{t("admin.shopify.title")}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("admin.shopify.subtitle")}</p>

      {/* Summary */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-shopify-summary">
        <SummaryCard label={t("admin.shopify.st.ok")} value={s.ok} cls="text-success" />
        <SummaryCard label={t("admin.shopify.st.pending")} value={s.pending} cls="text-amber-500" />
        <SummaryCard label={t("admin.shopify.st.manual")} value={s.manual} cls="text-muted-foreground" />
        <SummaryCard label={t("admin.shopify.st.fail")} value={s.fail} cls="text-destructive" />
      </div>

      <div className="mt-4 flex flex-wrap gap-4 rounded-lg border border-border bg-card p-4 text-sm">
        <span className="flex items-center gap-2"><Boxes className="h-4 w-4 text-brand" />{t("admin.shopify.connections")}: <b>{data.connections}</b></span>
        <span>{t("admin.shopify.apiVersion")}: <b className="font-mono">{data.api_version}</b></span>
        <span>{t("admin.shopify.scopesLabel")}: <b className="font-mono">{(data.scopes || []).join(", ")}</b></span>
      </div>

      {/* Checklist by category */}
      <div className="mt-6 space-y-6">
        {CATS.map((cat) => {
          const items = (data.items || []).filter((i) => i.cat === cat);
          if (!items.length) return null;
          return (
            <div key={cat} data-testid={`admin-shopify-cat-${cat}`}>
              <h2 className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">{t(`admin.shopify.cat.${cat}`)}</h2>
              <div className="divide-y divide-border rounded-lg border border-border bg-card">
                {items.map((it) => {
                  const meta = STATUS_META[it.status] || STATUS_META.manual;
                  const Icon = meta.icon;
                  return (
                    <div key={it.key} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`admin-shopify-item-${it.key}`}>
                      <span className="flex items-center gap-3 text-sm">
                        <Icon className={`h-4 w-4 shrink-0 ${meta.cls}`} />
                        {t(`admin.shopify.item.${it.key}`)}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.badge}`}>
                        {t(`admin.shopify.st.${it.status}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, cls }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className={`font-display text-2xl font-extrabold ${cls}`}>{value ?? 0}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

export default AdminShopify;
